'use client';
import { Divider, Input, Spinner, useDisclosure } from '@heroui/react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React, { useState } from 'react';
import { formatPrice } from '../utils/format-price';
import { formatDate } from '../utils/format-date';
import usePromoCodeStore, { PromoStatus } from '../store/use-promo-code-store';
import ModalTemplate from './modal-template';
import { useCustomizationContext } from '../customizations-provider';

export default function PromoCard() {
  const { applyCode, code, promoCampaign, status, isLoading } =
    usePromoCodeStore();
  const t = useTranslations('promoCard');
  const tValid = useTranslations('promoCard.valid');
  const c = useCustomizationContext();
  const arrowImage = c('promo.arrow');
  const { onOpen, onOpenChange, isOpen } = useDisclosure();
  const [promoCode, setPromoCode] = useState(code ?? '');
  const promoMessages = {
    valid: t('validPromoCode'),
    invalid: t('invalidPromoCode'),
    expired: t('expiredPromoCode'),
    empty: t('emptyPromoCode'),
    error: t('errorPromoCode'),
  };
  const isPromoCodeValid = status === PromoStatus.valid;
  const isPromoCodeSet = code !== null;

  return (
    <div
      className="outline outline-1 outline-[var(--color-border-default)] px-6 py-8 rounded-3.75 mt-6 md:mt-5 bg-[var(--color-card-primary)]"
      data-testid="energyCalculatorScreenPromoCodeCard"
    >
      <p
        data-testid="energyCalculatorScreenPromoCodeTitle"
        className="font-semibold text-xl pb-4"
      >
        {t('title')}
      </p>
      <form
        data-testid="promoCardForm"
        onSubmit={(event) => {
          event.preventDefault();
          applyCode(promoCode);
        }}
      >
        <Input
          data-testid="energyCalculatorScreenPromoCodeInputField"
          defaultValue={promoCode}
          endContent={
            <button
              data-testid="energyCalculatorPromoCodeApplyButton"
              className="bg-[var(--color-action-primary)] h-full w-[59px] rounded-r-[7px] flex justify-center items-center text-[var(--color-text-on-primary-action)]"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  data-testid="promo-spinner"
                  variant="default"
                  color="secondary"
                />
              ) : (
                <Image
                  data-testid="promo-arrow"
                  alt={arrowImage.description}
                  src={arrowImage.url}
                  width={arrowImage.width}
                  height={arrowImage.height}
                />
              )}
            </button>
          }
          classNames={{
            inputWrapper: `bg-transparent border ${!isPromoCodeSet || isPromoCodeValid ? 'border-[var(--color-border-default)]' : 'border-[var(--color-informative-error)]'} pr-0 py-0 rounded-lg h-[59px]`,
            innerWrapper: 'pb-0',
            description: `${isPromoCodeValid ? 'text-[var(--color-informative-success)]' : 'text-[var(--color-informative-error)]'}`,
          }}
          onValueChange={(value: string) => setPromoCode(value)}
          description={
            <div
              className="flex flex-row gap-x-1"
              data-testid="promoValidationMessage"
            >
              {isPromoCodeValid && (
                <Image
                  alt="green checkmark"
                  src="/images/green-check.svg"
                  height={16}
                  width={16}
                  data-testid="valid-promo-checkmark"
                />
              )}
              <span data-testid="promoValidationMessageText">
                {status && promoMessages[status]}
              </span>
            </div>
          }
        />
      </form>
      {isPromoCodeValid && (
        <div data-testid="energyCalculatorScreenPromoSection">
          <Divider className="mt-8 mb-6" />
          <div className="flex flex-row gap-x-4">
            <div>
              <p
                data-testid="energyCalculatorScreenPromoCodeDiscountTitle"
                className="font-semibold text-2xl leading-8.5 pb-1"
              >
                {promoCampaign?.title}
              </p>
              <p data-testid="promo-campaign-body">{promoCampaign?.bodyText}</p>
              <div
                data-testid="energyCalculatorScreenPromoCodeValue"
                className="flex flex-row justify-between mt-4"
              >
                <div
                  className={`font-heading text-[34px] md:text-[50px] leading-[46px] md:leading-[56px] mt-5 mb-8 rounded-[50px] py-2 md:py-[10px] px-8 md:px-9`}
                  style={{
                    backgroundColor: promoCampaign?.buttonBackgroundColor,
                  }}
                >
                  {formatPrice(promoCampaign?.value ?? 0, false)}
                </div>
                <div className="md:hidden min-h-[88px] min-w-[92px]">
                  <Image
                    data-testid="promo-campaign-image-mobile"
                    alt="gift image"
                    src="/images/gift-image-small.svg"
                    height="92"
                    width="88"
                  />
                </div>
              </div>
            </div>
            <div className="hidden md:flex justify-center min-h-[126px] min-w-[120px]">
              <Image
                data-testid="energyCalculatorScreenPromoCodeImage"
                alt="gift image"
                src="/images/gift-image-large.svg"
                height="126"
                width="120"
              />
            </div>
          </div>

          {promoCampaign?.validUntilUtc && (
            <p data-testid="energyCalculatorScreenPromoCodeExpire">
              {tValid.rich('expiration', {
                bonusEndDate: () => (
                  <span>{formatDate(promoCampaign.validUntilUtc)}</span>
                ),
              })}
            </p>
          )}
          <span
            data-testid="promo-campaign-conditions-popup-trigger"
            onClick={onOpen}
            className="pt-2 underline underline-offset-4 cursor-pointer"
          >
            <p data-testid="promo-campaign-conditions-title">
              {tValid('conditionsTitle')}
            </p>
          </span>
          <ModalTemplate
            onOpenChange={onOpenChange}
            isOpen={isOpen}
            title={tValid('conditionsTitle')}
          >
            <p data-testid="promo-campaign-conditions-body">
              {tValid('conditionsBody')}
            </p>
          </ModalTemplate>
        </div>
      )}
    </div>
  );
}
